import { Alert, Button, DangerModal, Flex, Hint, Link, Spacer } from '@lokalise/louis';
import cuid from 'cuid';
import { useCallback, useState } from 'react';

import type { Webhook } from '../../types';
import ExportAccordion from '../ExportAccordion';
import WebhookInput from './components/WebhookInput';
import { WebhookInputGrid, WebhookTitle } from './styles';

type WebhookWithId = {
    id: string;
    value: Webhook;
};

type Value = {
    switchWebhooks: boolean;
    webhooks: readonly Webhook[];
};

const addIds = (webhooks: readonly Webhook[]): readonly WebhookWithId[] =>
    webhooks.map((value) => ({
        id: cuid(),
        value,
    }));

const update = (webhooksWithId: readonly WebhookWithId[], value: Webhook, id: string): readonly WebhookWithId[] =>
    webhooksWithId.map((item) => {
        if (item.id === id) {
            return {
                id,
                value,
            };
        }

        return item;
    });

const add = (webhooksWithId: readonly WebhookWithId[]): readonly WebhookWithId[] => [
    ...webhooksWithId,
    {
        id: cuid(),
        value: {
            url: '',
            active: false,
        },
    },
];

const remove = (webhooksWithId: readonly WebhookWithId[], id: string): readonly WebhookWithId[] =>
    webhooksWithId.filter((item) => item.id !== id);

export interface WebhooksProps {
    value: Value;
    onChange: (value: Value) => void;
    projectId: string;
}

const Webhooks = ({ value, onChange, projectId }: WebhooksProps) => {
    const [webhooks, setWebhooks] = useState(() => addIds(value.webhooks));
    const [idToBeDeleted, setIdToBeDeleted] = useState<string | undefined>();

    const onRemoveHandler = (id: string | undefined) => {
        if (!id) {
            return;
        }

        setWebhooks((previousWebhooks) => remove(previousWebhooks, id));
        onChange({
            ...value,
            webhooks: remove(webhooks, id).map((item) => item.value),
        });
        setIdToBeDeleted(undefined);
    };

    const onChangeHandler = useCallback(
        (val: Webhook, id: string) => {
            setWebhooks((previousWebhooks) => update(previousWebhooks, val, id));

            onChange({
                ...value,
                webhooks: update(webhooks, val, id).map((item) => item.value),
            });
        },
        [onChange, webhooks, value],
    );

    return (
        <section aria-label="Export webhooks">
            <ExportAccordion
                name="webhooks"
                initiallyExpanded={value.switchWebhooks}
                projectId={projectId}
                onClick={(switchWebhooks) =>
                    onChange({
                        ...value,
                        switchWebhooks,
                    })
                }
                heading={
                    <>
                        <WebhookTitle>Webhook triggers </WebhookTitle>
                        <Hint placement="top">
                            Using webhooks is one of the ways to automatically integrate Lokalise into your deployment
                            workflow.{' '}
                            <Link
                                target="_blank"
                                rel="noreferrer"
                                href="https://developers.lokalise.com/docs/webhooks-guide"
                            >
                                Read more...
                            </Link>
                        </Hint>
                    </>
                }
            >
                <Spacer top={4}>
                    <Flex gap={2} direction="column">
                        {webhooks.length > 0 && (
                            <>
                                <WebhookInputGrid>
                                    {webhooks.map(({ id, value: webhook }) => (
                                        <WebhookInput
                                            key={id}
                                            id={id}
                                            value={webhook}
                                            onChange={onChangeHandler}
                                            onRemove={setIdToBeDeleted}
                                        />
                                    ))}
                                </WebhookInputGrid>
                                <DangerModal
                                    opened={!!idToBeDeleted}
                                    confirmText="Remove"
                                    onConfirm={() => onRemoveHandler(idToBeDeleted)}
                                    onClose={() => setIdToBeDeleted(undefined)}
                                >
                                    <Alert status="error">Are you sure you want to remove this webhook?</Alert>
                                </DangerModal>
                            </>
                        )}
                        <Button variant="secondary" size="sm" onClick={() => setWebhooks(add(webhooks))}>
                            Add webhook
                        </Button>
                    </Flex>
                </Spacer>
            </ExportAccordion>
        </section>
    );
};

export default Webhooks;
